import {
  BubbleController,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type Chart,
} from "chart.js";
import { ChevronRightIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Bubble } from "react-chartjs-2";

import { Badge } from "~/components/ui/badge";
import { buttonVariants } from "~/components/ui/button";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";

import { quadrantZonesPlugin, type QuadrantZonesOptions } from "./dashboardQuadrantZones";
import {
  AccountPanelHeading,
  AccountTableFrame,
  ColumnHelp,
  HEALTH_COLORS,
  HEALTH_RANK,
  HealthPill,
  MetricStack,
  SortHeading,
  TrafficLight,
  TrendSparkline,
  accessLabel,
  accessTone,
  accountBodyCellClass,
  accountHeadCellClass,
  alignmentTone,
  barFillClass,
  compareNullableSortValues,
  count,
  freshness,
  freshnessTone,
  meterTrackClass,
  months,
  navigatorNames,
  navigatorSummary,
  percent,
  toneDotClass,
} from "./accountManagementShared";
import type {
  AccountManagementThresholds,
  ClinicProvider,
  ClinicHealthClinic,
  ClinicHealthResponse,
  ClinicHealthSummary,
  ReviewSignal,
} from "./accountManagementTypes";
import {
  DashboardFilterField,
  DashboardFilterRow,
  DashboardSelect,
  KpiCard,
  KpiGrid,
} from "./dashboardKit";

ChartJS.register(LinearScale, PointElement, LineElement, BubbleController, Filler, Tooltip, Legend);

function PortfolioKpis({
  summary,
  reviewMaximumClinics,
  alignmentTargetRate,
}: {
  summary: ClinicHealthSummary;
  reviewMaximumClinics: number;
  alignmentTargetRate: number;
}) {
  const hasDelta = Number.isFinite(summary.alignedPatientsChange);
  const delta = hasDelta ? summary.alignedPatientsChange : 0;
  const deltaLabel = hasDelta
    ? `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta).toLocaleString()} vs last mo`
    : null;
  return (
    <KpiGrid>
      <KpiCard
        label="Needs attention"
        value={count(summary.needsAttentionCount)}
        footnote={`at-risk, Watch, or acute decline · top ${reviewMaximumClinics} shown`}
      />
      <KpiCard
        label="Alignment rate"
        value={percent(summary.alignmentRate)}
        footnote={`${count(summary.alignedPatients)} / ${count(summary.totalPatients)} active · target ≥${percent(alignmentTargetRate)}`}
      />
      <KpiCard
        label="Aligned patients"
        value={count(summary.alignedPatients)}
        delta={deltaLabel}
        {...(hasDelta ? { deltaDirection: (delta < 0 ? "down" : "up") as "up" | "down" } : {})}
      />
      <KpiCard
        label="Clinics in book"
        value={count(summary.clinicCount)}
        footnote={`${count(summary.navigatorCount)} navigator${summary.navigatorCount === 1 ? "" : "s"}`}
      />
      <KpiCard
        label="Median tenure"
        value={months(summary.medianTenureMonths)}
        footnote="aligned duration"
      />
    </KpiGrid>
  );
}

const SIGNAL_ORDER: Record<string, number> = {
  "health-grade": 0,
  "patient-add-decline": 1,
  "retell-pickup-decline": 2,
  "alignment-target": 0,
};

function signalTone(signal: ReviewSignal): "at-risk" | "watch" | "opportunity" | "neutral" {
  if (signal.key === "health-grade") {
    return signal.severity === "at-risk" ? "at-risk" : "watch";
  }
  if (signal.key === "patient-add-decline") return "at-risk";
  if (signal.key === "retell-pickup-decline") return "watch";
  if (signal.key === "alignment-target") return "opportunity";
  return "neutral";
}

function SignalChip({ signal }: { signal: ReviewSignal }) {
  const tone = signalTone(signal);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
        tone === "at-risk" && "border-red-200 bg-red-50 text-red-700",
        tone === "watch" && "border-amber-200 bg-amber-50 text-amber-800",
        tone === "opportunity" && "border-blue-200 bg-blue-50 text-blue-700",
        tone === "neutral" && "border-border bg-muted text-muted-foreground",
      )}
      title={signal.detail}
    >
      <i
        className={cn(
          "inline-block size-1.5 rounded-full",
          tone === "at-risk" && "bg-red-500",
          tone === "watch" && "bg-amber-500",
          tone === "opportunity" && "bg-blue-500",
          tone === "neutral" && "bg-slate-400",
        )}
        aria-hidden="true"
      />
      {signal.label}
    </span>
  );
}

function ReviewClinicCard({
  clinic,
  index,
  isOpportunity,
  signals,
}: {
  clinic: ClinicHealthClinic;
  index: number;
  isOpportunity: boolean;
  signals: ReviewSignal[];
}) {
  const orderedSignals = [...signals].sort(
    (left, right) => (SIGNAL_ORDER[left.key] ?? 99) - (SIGNAL_ORDER[right.key] ?? 99),
  );
  const evidence = orderedSignals
    .map((signal) => signal.detail)
    .filter(Boolean)
    .join(" ");

  return (
    <article
      className={cn(
        "flex flex-col gap-3 rounded-lg border bg-background p-4 shadow-xs/5",
        isOpportunity ? "border-blue-200/80 border-l-4 border-l-blue-500" : "border-border/70",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Badge
            size="sm"
            variant={isOpportunity ? "info" : "outline"}
            className="mt-0.5 tabular-nums"
            aria-hidden="true"
          >
            {index + 1}
          </Badge>
          <div className="min-w-0 space-y-1">
            <h3
              className="truncate text-sm font-semibold tracking-tight text-foreground"
              title={clinic.name}
            >
              {clinic.name}
            </h3>
            <p className="text-xs text-muted-foreground">
              {[clinic.state, navigatorSummary(clinic.navigator)].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
        <a
          className={cn(buttonVariants({ size: "xs", variant: "ghost" }), "shrink-0")}
          href={`#clinic-${clinic.id}`}
          aria-label={`${isOpportunity ? "Review opportunity for" : "Review attention for"} ${clinic.name}`}
        >
          Review
          <ChevronRightIcon />
        </a>
      </div>
      {orderedSignals.length ? (
        <div className="flex flex-wrap gap-1.5 pl-8">
          {orderedSignals.map((signal) => (
            <SignalChip key={signal.key} signal={signal} />
          ))}
        </div>
      ) : null}
      {evidence ? (
        <Collapsible className="pl-8">
          <CollapsibleTrigger className="text-xs font-medium text-muted-foreground hover:text-foreground">
            Evidence
          </CollapsibleTrigger>
          <CollapsiblePanel>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{evidence}</p>
          </CollapsiblePanel>
        </Collapsible>
      ) : null}
    </article>
  );
}

function ReviewBand({
  clinics,
  maximumClinics,
  kind,
  isFiltered,
}: {
  clinics: ClinicHealthClinic[];
  maximumClinics: number;
  kind: "attention" | "opportunity";
  isFiltered: boolean;
}) {
  const isOpportunity = kind === "opportunity";
  const priorityKey = isOpportunity ? "hasOpportunity" : "needsAttention";
  const priorityFlagKey = isOpportunity ? "opportunityPriority" : "attentionPriority";
  const scoreKey = isOpportunity ? "reviewOpportunityScore" : "reviewAttentionScore";
  const signalsKey = isOpportunity ? "opportunitySignals" : "attentionSignals";
  const reviews = [...clinics]
    .filter((clinic) => clinic[priorityKey])
    .sort(
      (left, right) =>
        (isFiltered ? 0 : Number(right[priorityFlagKey]) - Number(left[priorityFlagKey])) ||
        right[scoreKey] - left[scoreKey] ||
        right.totalPatients - left.totalPatients ||
        left.name.localeCompare(right.name),
    );
  const visibleLimit = isOpportunity ? Math.min(maximumClinics, 3) : maximumClinics;
  const visibleReviews = reviews.slice(0, visibleLimit);

  return (
    <section
      className={cn(
        "rounded-lg border p-5 shadow-xs",
        isOpportunity ? "border-blue-200/80 bg-blue-50/40" : "border-border/70 bg-background/80",
      )}
      data-review-kind={kind}
    >
      <AccountPanelHeading
        meta={`${reviews.length} clinics · ${
          isOpportunity
            ? "closest to the alignment target"
            : "highest-impact health and decline signals"
        }`}
      >
        {isOpportunity ? "Growth opportunities" : "Needs attention"}
      </AccountPanelHeading>
      {reviews.length ? (
        <div className="flex flex-col gap-3">
          {visibleReviews.map((clinic, index) => (
            <ReviewClinicCard
              key={clinic.id}
              clinic={clinic}
              index={index}
              isOpportunity={isOpportunity}
              signals={clinic[signalsKey]}
            />
          ))}
          {reviews.length > visibleReviews.length ? (
            <p className="text-xs text-muted-foreground">
              +{reviews.length - visibleReviews.length} more ranked clinics in the scorecard below
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {isOpportunity
            ? "No medium or large clinics are currently just below target."
            : "No clinics currently have a health or acute-decline signal."}
        </p>
      )}
    </section>
  );
}
function sortableValue(clinic: ClinicHealthClinic, key: string): unknown {
  if (key === "health") return HEALTH_RANK[clinic.grade];
  if (key === "clinic") return clinic.name.toLowerCase();
  if (key === "navigator") return clinic.navigator.toLowerCase();
  if (key === "aiCallingApproved") return clinic.aiCallingApproved ?? null;
  if (key === "trend") {
    const first = clinic.alignedTrend[0]?.alignedPatients;
    const latest = clinic.alignedTrend.at(-1)?.alignedPatients;
    return first === undefined || latest === undefined ? null : latest - first;
  }
  if (key === "readiness") {
    return [clinic.emrAccess, clinic.reportAccess, clinic.rpaStatus].join(":");
  }
  return (clinic as Record<string, unknown>)[key];
}

export type ClinicHealthSort = { key: string; direction: "asc" | "desc" };

export function sortClinicHealthClinics(
  clinics: ClinicHealthClinic[],
  sort: ClinicHealthSort,
): ClinicHealthClinic[] {
  return [...clinics].sort((left, right) => {
    const leftValue = sortableValue(left, sort.key);
    const rightValue = sortableValue(right, sort.key);
    return compareNullableSortValues(leftValue, rightValue, sort.direction);
  });
}

function PortfolioHealthQuadrant({
  clinics,
  targetRate,
}: {
  clinics: ClinicHealthClinic[];
  targetRate: number;
}) {
  const chartRef = useRef<Chart<"bubble"> | null>(null);
  const maximumPatients = Math.max(...clinics.map((clinic) => clinic.totalPatients), 1);
  const radiusScale = 24 / Math.sqrt(maximumPatients);
  const sortedNetNew = clinics
    .map((clinic) => Number(clinic.netNewAligned30d) || 0)
    .sort((left, right) => left - right);
  const netNewMidpoint = Math.floor(sortedNetNew.length / 2);
  const netNewMedian = !sortedNetNew.length
    ? 0
    : sortedNetNew.length % 2 === 1
      ? (sortedNetNew[netNewMidpoint] ?? 0)
      : ((sortedNetNew[netNewMidpoint - 1] ?? 0) + (sortedNetNew[netNewMidpoint] ?? 0)) / 2;
  const gradeLabels = {
    healthy: "Healthy",
    watch: "Watch",
    "at-risk": "At risk",
    monitoring: "Monitoring",
  } as const;
  type GradeKey = keyof typeof gradeLabels;
  const data = {
    datasets: (Object.keys(gradeLabels) as GradeKey[]).map((grade) => ({
      label: gradeLabels[grade],
      data: clinics
        .filter((clinic) => clinic.grade === grade)
        .map((clinic) => ({
          x: clinic.alignmentRate * 100,
          y: Math.max(Number(clinic.netNewAligned30d) || 0),
          r: Math.sqrt(clinic.totalPatients) * radiusScale,
          clinic,
        })),
      backgroundColor: `${HEALTH_COLORS[grade]}b8`,
      borderColor: (context: { raw?: unknown }) => {
        const clinic = (context.raw as { clinic?: ClinicHealthClinic } | undefined)?.clinic;
        return clinic?.attentionSignals.some((signal) => signal.key === "patient-add-decline")
          ? "#b42318"
          : HEALTH_COLORS[grade];
      },
      borderWidth: (context: { raw?: unknown }) => {
        const clinic = (context.raw as { clinic?: ClinicHealthClinic } | undefined)?.clinic;
        return clinic?.attentionSignals.some((signal) => signal.key === "patient-add-decline")
          ? 3
          : 1.5;
      },
    })),
  } as const;

  return (
    <section className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-xs">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Clinic health · portfolio
          </span>
          <h2>Portfolio health quadrant</h2>
        </div>
        <span>x = alignment · y = net-new aligned · area = marketable patients</span>
      </div>
      <p className="mb-3 text-sm text-muted-foreground">
        Large clinics in the lower-left need the fastest intervention; upper-right clinics are the
        reference set to learn from.
      </p>
      <div className="relative h-[420px] min-w-0">
        <Bubble
          ref={chartRef}
          data={data as never}
          plugins={[quadrantZonesPlugin]}
          options={
            {
              responsive: true,
              maintainAspectRatio: false,
              onClick: (
                _event: unknown,
                elements: Array<{ datasetIndex: number; index: number }>,
              ) => {
                const element = elements[0];
                if (!element || element.datasetIndex == null || element.index == null) return;
                const point = chartRef.current?.data.datasets[element.datasetIndex]?.data[
                  element.index
                ] as { clinic?: ClinicHealthClinic } | undefined;
                const clicked = point?.clinic;
                document
                  .getElementById(`clinic-${clicked?.id}`)
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              },
              layout: { padding: { top: 8, right: 8 } },
              interaction: { mode: "nearest", intersect: true },
              plugins: {
                quadrantZones: {
                  x: targetRate * 100,
                  y: netNewMedian,
                  xLabel: `TARGET ${Math.round(targetRate * 100)}%`,
                  yLabel: "MEDIAN NEW-ALIGNED",
                  topLeft: { label: "RAMPING", fill: "rgba(220, 76, 53, .05)" },
                  topRight: { label: "GROW / REFERENCE", fill: "rgba(16, 185, 129, .06)" },
                  bottomLeft: { label: "FIX NOW", fill: "rgba(220, 76, 53, .10)" },
                  bottomRight: { label: "PROTECT / STEADY", fill: "rgba(245, 158, 11, .07)" },
                } satisfies QuadrantZonesOptions,
                legend: {
                  position: "bottom",
                  labels: {
                    usePointStyle: true,
                    boxWidth: 9,
                    generateLabels: (chart: Chart) =>
                      ChartJS.defaults.plugins.legend.labels.generateLabels(chart).map((item) => {
                        const grade = (Object.entries(gradeLabels).find(
                          ([, label]) => label === item.text,
                        )?.[0] ?? "monitoring") as GradeKey;
                        return {
                          ...item,
                          strokeStyle: HEALTH_COLORS[grade] || item.strokeStyle,
                          lineWidth: 1.5,
                        };
                      }),
                  },
                },
                tooltip: {
                  callbacks: {
                    title: (items: Array<{ raw?: unknown }>) => {
                      const raw = items[0]?.raw as { clinic?: ClinicHealthClinic } | undefined;
                      return raw?.clinic?.name || "";
                    },
                    label: (context: { raw?: unknown }) => {
                      const clinic = (context.raw as { clinic: ClinicHealthClinic }).clinic;
                      return [
                        `Alignment: ${percent(clinic.alignmentRate, 1)}`,
                        `Net-new aligned (30d): +${count(clinic.netNewAligned30d)}`,
                        `Marketable patients: ${count(clinic.marketablePatients ?? clinic.totalPatients)}`,
                        `Navigator: ${navigatorSummary(clinic.navigator)}`,
                        ...(clinic.attentionSignals.some(
                          (signal) => signal.key === "patient-add-decline",
                        )
                          ? ["Priority signal: acute patient-flow decline"]
                          : []),
                      ];
                    },
                  },
                },
              },
              scales: {
                x: {
                  suggestedMin: 0,
                  suggestedMax: Math.max(40, targetRate * 100),
                  grace: "25%",
                  title: { display: true, text: "Alignment rate" },
                  ticks: {
                    callback: (value: string | number) => {
                      const numeric = Number(value);
                      return numeric < 0 || numeric > 100 ? "" : `${numeric}%`;
                    },
                  },
                  grid: { color: "#eee9df" },
                },
                y: {
                  suggestedMin: 0,
                  suggestedMax: 5,
                  grace: "25%",
                  title: { display: true, text: "Net-new aligned (30d)" },
                  ticks: {
                    callback: (value: string | number) => {
                      const numeric = Number(value);
                      return numeric < 0 ? "" : `${numeric}`;
                    },
                  },
                  grid: { color: "#eee9df" },
                },
              },
            } as never
          }
        />
      </div>
      <small className="mt-2 block text-xs text-muted-foreground">
        Fill shows current health grade; a red outline marks an acute patient-flow decline. The
        y-axis is the absolute number of net-new aligned patients in the last 30 days. Bubble area
        is proportional to marketable patients. Select a bubble to jump to its scorecard row.
      </small>
    </section>
  );
}

export type ProviderSortKey =
  | "provider"
  | "patientCount"
  | "alignedPatients"
  | "observedAlignmentRate";

export type ProviderSort = { key: ProviderSortKey; direction: "asc" | "desc" };

const PROVIDER_COLUMNS: Array<{
  key: ProviderSortKey;
  label: string;
  definition: string;
}> = [
  {
    key: "provider",
    label: "Provider",
    definition: "The provider name and whether the identity was verified with an NPI.",
  },
  {
    key: "patientCount",
    label: "Attributed patients",
    definition: "Patients the available data connects to this provider.",
  },
  {
    key: "alignedPatients",
    label: "Aligned patients",
    definition: "Attributed patients who are currently aligned with the clinic.",
  },
  {
    key: "observedAlignmentRate",
    label: "Observed alignment",
    definition: "The share of this provider’s attributed patients who are aligned.",
  },
];

function providerSortableValue(provider: ClinicProvider, key: ProviderSortKey) {
  if (key === "provider") return provider.name.toLowerCase();
  return provider[key];
}

export function sortProviderRows(providers: ClinicProvider[], sort: ProviderSort) {
  return [...providers].sort((left, right) => {
    const comparison = compareNullableSortValues(
      providerSortableValue(left, sort.key),
      providerSortableValue(right, sort.key),
      sort.direction,
    );
    return comparison || left.name.localeCompare(right.name);
  });
}

function ProviderAlignmentPanel({
  clinic,
  onClose,
}: {
  clinic: ClinicHealthClinic;
  onClose: () => void;
}) {
  const [sort, setSort] = useState<ProviderSort>({
    key: "patientCount",
    direction: "desc",
  });
  const sortedProviders = useMemo(
    () => sortProviderRows(clinic.providers, sort),
    [clinic.providers, sort],
  );

  function updateSort(column: string) {
    const nextColumn = PROVIDER_COLUMNS.find(({ key }) => key === column)?.key;
    if (!nextColumn) return;
    setSort((current) => ({
      key: nextColumn,
      direction: current.key === nextColumn && current.direction === "desc" ? "asc" : "desc",
    }));
  }

  return (
    <section
      id="provider-alignment-panel"
      className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-xs"
      aria-labelledby="provider-alignment-title"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Clinic drilldown
          </span>
          <h2 id="provider-alignment-title">Provider alignment</h2>
          <p>{clinic.name}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close provider alignment">
          Close
        </button>
      </div>
      <p className="mb-3 text-sm text-muted-foreground">
        Observed rates use only patients attributed to each provider. Name-only rows can split one
        provider across spelling or credential variants and may show only part of their panel.
      </p>
      {clinic.providers.length ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse text-sm">
            <thead>
              <tr>
                {PROVIDER_COLUMNS.map(({ key, label, definition }, index) => (
                  <th
                    key={key}
                    className={accountHeadCellClass(index === 0)}
                    aria-sort={
                      sort.key === key
                        ? sort.direction === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <span className="inline-flex items-center gap-1">
                      <SortHeading column={key} label={label} sort={sort} onSort={updateSort} />
                      <ColumnHelp label={label} definition={definition} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedProviders.map((provider) => (
                <tr key={provider.id} className={provider.unknown ? "provider-unknown" : ""}>
                  <td>
                    <strong>{provider.name}</strong>
                    <small>
                      {provider.unknown
                        ? "Provider attribution unavailable"
                        : provider.grouped
                          ? "Grouped remainder"
                          : provider.verified
                            ? "NPI verified"
                            : "Name only · identity unverified"}
                    </small>
                  </td>
                  <td>{count(provider.patientCount)}</td>
                  <td>{count(provider.alignedPatients)}</td>
                  <td>
                    {provider.unknown ? (
                      <span className="text-xs text-muted-foreground">Unscored</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={meterTrackClass("w-[54px]")} aria-hidden="true">
                          <i
                            className="block h-full rounded-full bg-slate-500"
                            style={{
                              width: `${
                                Math.min(Math.max(provider.observedAlignmentRate ?? 0, 0), 1) * 100
                              }%`,
                            }}
                          />
                        </span>
                        <strong>{percent(provider.observedAlignmentRate)}</strong>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No provider attribution data available.</p>
      )}
    </section>
  );
}

function ClinicScorecard({
  clinics,
  thresholds,
}: {
  clinics: ClinicHealthClinic[];
  thresholds: AccountManagementThresholds;
}) {
  const [sort, setSort] = useState<ClinicHealthSort>({
    key: "alignedPatients",
    direction: "desc",
  });
  const [selectedClinicId, setSelectedClinicId] = useState<string | null>(null);
  const selectedClinic = clinics.find((clinic) => clinic.id === selectedClinicId);
  useEffect(() => {
    if (selectedClinicId && !clinics.some((clinic) => clinic.id === selectedClinicId)) {
      setSelectedClinicId(null);
    }
  }, [clinics, selectedClinicId]);
  useEffect(() => {
    if (!selectedClinicId) return undefined;
    const frame = requestAnimationFrame(() => {
      document
        .getElementById("provider-alignment-panel")
        ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedClinicId]);
  const sortedClinics = useMemo(() => {
    return sortClinicHealthClinics(clinics, sort);
  }, [clinics, sort]);

  function updateSort(key: string) {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  }

  function toggleClinic(clinicId: string) {
    setSelectedClinicId((current) => (current === clinicId ? null : clinicId));
  }

  const headings = [
    {
      key: "clinic",
      label: "Clinic",
      definition: "Clinic organization name, state, and assigned care navigator.",
    },
    {
      key: "health",
      label: "Health",
      definition:
        "A quick overall status. Green means things look good, amber means keep an eye on the clinic, and red means it likely needs help.",
    },
    {
      key: "readiness",
      label: "Readiness",
      definition:
        "Whether the team can get into the clinic’s medical records and reports, and whether automation is ready to run.",
    },
    {
      key: "aiCallingApproved",
      label: "AI calling",
      definition:
        "Whether the organization allows AI-assisted marketing and outreach. Green is approved; red is not approved.",
    },
    {
      key: "alignedPatients",
      label: "Alignment",
      definition: `Patients currently aligned with the clinic out of all patients who can be worked. The goal is at least ${percent(thresholds.alignmentTargetRate)}.`,
    },
    {
      key: "freshnessDays",
      label: "Freshness",
      definition:
        "How many days it has been since we last saw DCMP billing activity for the clinic.",
    },
    {
      key: "trend",
      label: "6-mo trend",
      definition:
        "How the clinic’s number of aligned patients has changed over the last six months.",
    },
  ];

  return (
    <>
      <section className="rounded-lg border border-border/70 bg-background/80 p-5 shadow-xs">
        <AccountPanelHeading meta="Grouped triage columns · click a header to sort">
          Clinic detail scorecard
        </AccountPanelHeading>
        <AccountTableFrame aria-label="Clinic detail scorecard" minWidthClass="min-w-[1080px]">
          <thead>
            <tr>
              {headings.map(({ key, label, definition }, index) => (
                <th
                  key={key}
                  className={accountHeadCellClass(index === 0)}
                  aria-sort={
                    sort.key === key
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  <span className="inline-flex items-center gap-1">
                    <SortHeading column={key} label={label} sort={sort} onSort={updateSort} />
                    <ColumnHelp label={label} definition={definition} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedClinics.map((clinic) => (
              <tr key={clinic.id} id={`clinic-${clinic.id}`} className="group">
                <td className={accountBodyCellClass({ sticky: true })}>
                  <button
                    type="button"
                    className="min-w-0 text-left"
                    onClick={() => toggleClinic(clinic.id)}
                    aria-expanded={selectedClinicId === clinic.id}
                    aria-label={`View provider alignment for ${clinic.name}`}
                  >
                    <span className="block truncate font-semibold" title={clinic.name}>
                      {clinic.name}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">
                      {clinic.state} · {navigatorSummary(clinic.navigator)}
                    </span>
                  </button>
                </td>
                <td className={accountBodyCellClass()}>
                  <HealthPill grade={clinic.grade} />
                </td>
                <td className={accountBodyCellClass()}>
                  <ReadinessCell clinic={clinic} />
                </td>
                <td className={accountBodyCellClass()}>
                  <Badge
                    variant={
                      clinic.aiCallingApproved === true
                        ? "success"
                        : clinic.aiCallingApproved === false
                          ? "error"
                          : "outline"
                    }
                    className="rounded-full px-2 py-0.5"
                  >
                    {clinic.aiCallingApproved === true
                      ? "Approved"
                      : clinic.aiCallingApproved === false
                        ? "Not approved"
                        : "Unknown"}
                  </Badge>
                </td>
                <td className={accountBodyCellClass({ numeric: true })}>
                  <AlignmentCell clinic={clinic} thresholds={thresholds} />
                </td>
                <td className={accountBodyCellClass({ numeric: true })}>
                  <TrafficLight
                    tone={
                      clinic.grade === "monitoring"
                        ? "neutral"
                        : freshnessTone(clinic.freshnessDays, thresholds)
                    }
                    label={freshness(clinic.freshnessDays)}
                  />
                </td>
                <td className={accountBodyCellClass()}>
                  <TrendSparkline
                    points={clinic.alignedTrend}
                    label={`${clinic.name} aligned patients over six months`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </AccountTableFrame>
      </section>
      {selectedClinic ? (
        <ProviderAlignmentPanel clinic={selectedClinic} onClose={() => setSelectedClinicId(null)} />
      ) : null}
    </>
  );
}

function ReadinessCell({ clinic }: { clinic: ClinicHealthClinic }) {
  const rpaTone =
    clinic.rpaStatus === "active"
      ? "good"
      : clinic.rpaStatus === "not_started"
        ? "warn"
        : "neutral";
  const rpaTitle =
    clinic.rpaStatus === "active"
      ? "RPA active"
      : clinic.rpaStatus === "not_started"
        ? "RPA not started"
        : "RPA unknown";
  const signals = [
    {
      key: "emr",
      label: "EMR",
      title: accessLabel("Access", clinic.emrAccess),
      tone: accessTone(clinic.emrAccess),
    },
    {
      key: "reports",
      label: "Rpt",
      title: accessLabel("Reports", clinic.reportAccess),
      tone: accessTone(clinic.reportAccess, "warn"),
    },
    { key: "rpa", label: "RPA", title: rpaTitle, tone: rpaTone },
  ] as const;

  return (
    <div className="min-w-[9rem] space-y-1.5">
      <p className="truncate font-medium" title={clinic.emrName || "Not recorded"}>
        {clinic.emrName || "Not recorded"}
      </p>
      <div className="flex flex-wrap gap-x-2.5 gap-y-1">
        {signals.map((signal) => (
          <span
            key={signal.key}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"
            title={signal.title}
          >
            <i className={toneDotClass(signal.tone)} aria-hidden="true" />
            {signal.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function AlignmentCell({
  clinic,
  thresholds,
}: {
  clinic: ClinicHealthClinic;
  thresholds: AccountManagementThresholds;
}) {
  const tone =
    clinic.grade === "monitoring" ? "neutral" : alignmentTone(clinic.alignmentRate, thresholds);
  return (
    <div className="min-w-[9.5rem] space-y-1.5">
      <div className="flex items-center gap-2">
        <i className={toneDotClass(tone)} aria-hidden="true" />
        <span className="font-semibold tabular-nums">{percent(clinic.alignmentRate)}</span>
      </div>
      <span className={meterTrackClass("w-full max-w-[7.5rem]")} aria-hidden="true">
        <i
          className={barFillClass(tone)}
          style={{ width: `${Math.min(clinic.alignmentRate / 0.5, 1) * 100}%` }}
        />
      </span>
      <p className="text-[11px] text-muted-foreground tabular-nums">
        {count(clinic.alignedPatients)} aligned · {count(clinic.totalPatients)} marketable
      </p>
    </div>
  );
}

export type OperationsSortKey =
  | "clinic"
  | "retellCalls30d"
  | "retellAnswered30d"
  | "retellVoicemail30d"
  | "retellIntakesScheduled30d";

export type OperationsSort = { key: OperationsSortKey; direction: "asc" | "desc" };

const OPERATIONS_COLUMNS: Array<{
  key: OperationsSortKey;
  label: string;
  definition: string;
}> = [
  {
    key: "clinic",
    label: "Clinic",
    definition: "The clinic name and the state where it is located.",
  },
  {
    key: "retellCalls30d",
    label: "Calls 30d",
    definition: "AI phone calls placed for this clinic in the last 30 days.",
  },
  {
    key: "retellAnswered30d",
    label: "Answered",
    definition:
      "Calls answered by a person in the last 30 days; the smaller number is the share of all calls.",
  },
  {
    key: "retellVoicemail30d",
    label: "Voicemail",
    definition:
      "Calls that reached voicemail in the last 30 days; the smaller number is the share of all calls.",
  },
  {
    key: "retellIntakesScheduled30d",
    label: "Intakes",
    definition:
      "Intake appointments scheduled from answered calls in the last 30 days; the smaller number is the share of answered calls.",
  },
];

function operationsSortableValue(clinic: ClinicHealthClinic, key: OperationsSortKey) {
  if (key === "clinic") return clinic.name.toLowerCase();
  return clinic[key];
}

export function sortOperationsClinics(
  clinics: ClinicHealthClinic[],
  sort: OperationsSort,
): ClinicHealthClinic[] {
  return [...clinics].sort((left, right) => {
    const comparison = compareNullableSortValues(
      operationsSortableValue(left, sort.key),
      operationsSortableValue(right, sort.key),
      sort.direction,
    );
    return comparison || left.name.localeCompare(right.name);
  });
}

function OperationsMetrics({ clinics }: { clinics: ClinicHealthClinic[] }) {
  const [sort, setSort] = useState<OperationsSort>({
    key: "retellCalls30d",
    direction: "desc",
  });
  const sortedClinics = useMemo(() => sortOperationsClinics(clinics, sort), [clinics, sort]);

  function updateSort(column: string) {
    const nextColumn = OPERATIONS_COLUMNS.find(({ key }) => key === column)?.key;
    if (!nextColumn) return;
    setSort((current) => ({
      key: nextColumn,
      direction: current.key === nextColumn && current.direction === "desc" ? "asc" : "desc",
    }));
  }

  return (
    <section className="rounded-lg border border-border/70 bg-background/80 p-5 shadow-xs">
      <AccountPanelHeading meta="Trailing-30-day Retell outcomes">
        Operations metrics · Retell
      </AccountPanelHeading>
      <AccountTableFrame aria-label="Operations metrics for Retell" minWidthClass="min-w-[760px]">
        <thead>
          <tr>
            {OPERATIONS_COLUMNS.map(({ key, label, definition }, index) => (
              <th
                key={key}
                className={accountHeadCellClass(index === 0)}
                aria-sort={
                  sort.key === key
                    ? sort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                <span className="inline-flex items-center gap-1">
                  <SortHeading column={key} label={label} sort={sort} onSort={updateSort} />
                  <ColumnHelp label={label} definition={definition} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedClinics.map((clinic) => (
            <tr key={clinic.id} className="group">
              <td className={accountBodyCellClass({ sticky: true })}>
                <span className="block truncate font-semibold" title={clinic.name}>
                  {clinic.name}
                </span>
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  {clinic.state}
                </span>
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                <MetricStack value={count(clinic.retellCalls30d)} />
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                <MetricStack
                  value={count(clinic.retellAnswered30d)}
                  detail={percent(clinic.retellAnswerRate30d)}
                />
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                <MetricStack
                  value={count(clinic.retellVoicemail30d)}
                  detail={percent(clinic.retellVoicemailRate30d)}
                />
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                <MetricStack
                  value={count(clinic.retellIntakesScheduled30d)}
                  detail={percent(clinic.retellIntakeAnswerRate30d)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </AccountTableFrame>
    </section>
  );
}

export function ClinicHealthPanel({ data }: { data: ClinicHealthResponse }) {
  const [clinicFilter, setClinicFilter] = useState("all");
  const [navigatorFilter, setNavigatorFilter] = useState("all");
  const clinicOptions = useMemo(
    () => [...data.clinics].sort((left, right) => left.name.localeCompare(right.name)),
    [data.clinics],
  );
  const navigatorOptions = useMemo(
    () =>
      [...new Set(data.clinics.flatMap((clinic) => navigatorNames(clinic.navigator)))].sort(
        (left, right) => left.localeCompare(right),
      ),
    [data.clinics],
  );
  const visibleClinics = useMemo(
    () =>
      data.clinics.filter(
        (clinic) =>
          (clinicFilter === "all" || clinic.id === clinicFilter) &&
          (navigatorFilter === "all" || navigatorNames(clinic.navigator).includes(navigatorFilter)),
      ),
    [clinicFilter, data.clinics, navigatorFilter],
  );
  const visibleSummary = useMemo(() => {
    const totalPatients = visibleClinics.reduce((sum, clinic) => sum + clinic.totalPatients, 0);
    const alignedPatients = visibleClinics.reduce((sum, clinic) => sum + clinic.alignedPatients, 0);
    const missingMbiPatientCount = visibleClinics.reduce(
      (sum, clinic) => sum + clinic.missingMbiPatientCount,
      0,
    );
    const clinicalVisitPatientCount = visibleClinics.reduce(
      (sum, clinic) => sum + clinic.clinicalVisitPatientCount,
      0,
    );
    const icd10PatientCount = visibleClinics.reduce(
      (sum, clinic) => sum + clinic.icd10PatientCount,
      0,
    );
    const missingIcd10PatientCount = visibleClinics.reduce(
      (sum, clinic) => sum + clinic.missingIcd10PatientCount,
      0,
    );
    const tenures = visibleClinics
      .map((clinic) => clinic.medianTenureMonths)
      .filter((value): value is number => Number.isFinite(value))
      .sort((left, right) => left - right);
    const middle = Math.floor(tenures.length / 2);
    const medianTenureMonths: number | null = tenures.length
      ? tenures.length % 2
        ? (tenures[middle] ?? null)
        : ((tenures[middle - 1] ?? 0) + (tenures[middle] ?? 0)) / 2
      : null;
    const alignedPatientsChange = visibleClinics.reduce((sum, clinic) => {
      const latest = clinic.alignedTrend.at(-1)?.alignedPatients;
      const previous = clinic.alignedTrend.at(-2)?.alignedPatients;
      return Number.isFinite(latest) && Number.isFinite(previous)
        ? sum + (latest as number) - (previous as number)
        : sum;
    }, 0);
    return {
      ...data.summary,
      clinicCount: visibleClinics.length,
      navigatorCount:
        navigatorFilter === "all"
          ? new Set(visibleClinics.flatMap((clinic) => navigatorNames(clinic.navigator))).size
          : 1,
      totalPatients,
      alignedPatients,
      alignedPatientsChange,
      alignmentRate: totalPatients > 0 ? alignedPatients / totalPatients : null,
      medianTenureMonths,
      needsAttentionCount: visibleClinics.filter((clinic) => clinic.needsAttention).length,
      opportunityCount: visibleClinics.filter((clinic) => clinic.hasOpportunity).length,
      clinicalVisitPatientCount,
      clinicalVisitCoverageRate:
        totalPatients > 0 ? clinicalVisitPatientCount / totalPatients : null,
      missingMbiPatientCount,
      missingMbiRate: totalPatients > 0 ? missingMbiPatientCount / totalPatients : null,
      icd10PatientCount,
      icd10CoverageRate:
        clinicalVisitPatientCount > 0 ? icd10PatientCount / clinicalVisitPatientCount : null,
      missingIcd10PatientCount,
      missingIcd10Rate:
        clinicalVisitPatientCount > 0 ? missingIcd10PatientCount / clinicalVisitPatientCount : null,
    };
  }, [data.summary, navigatorFilter, visibleClinics]);

  function updateClinicFilter(nextClinicId: string) {
    setClinicFilter(nextClinicId);
    if (nextClinicId === "all" || navigatorFilter === "all") return;
    const nextClinic = data.clinics.find((clinic) => clinic.id === nextClinicId);
    if (!navigatorNames(nextClinic?.navigator).includes(navigatorFilter)) {
      setNavigatorFilter("all");
    }
  }

  function updateNavigatorFilter(nextNavigator: string) {
    setNavigatorFilter(nextNavigator);
    if (nextNavigator === "all" || clinicFilter === "all") return;
    const selectedClinic = data.clinics.find((clinic) => clinic.id === clinicFilter);
    if (!navigatorNames(selectedClinic?.navigator).includes(nextNavigator)) {
      setClinicFilter("all");
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <DashboardFilterRow trailing={`As of ${data.asOf}`}>
        <DashboardFilterField htmlFor="account-management-clinic-filter" label="Clinic">
          <DashboardSelect
            id="account-management-clinic-filter"
            value={clinicFilter}
            onChange={(event) => updateClinicFilter(event.target.value)}
          >
            <option value="all">All clinics ({data.clinics.length})</option>
            {clinicOptions.map((clinic) => (
              <option key={clinic.id} value={clinic.id}>
                {clinic.name}
              </option>
            ))}
          </DashboardSelect>
        </DashboardFilterField>
        <DashboardFilterField htmlFor="account-management-navigator-filter" label="Navigator">
          <DashboardSelect
            id="account-management-navigator-filter"
            value={navigatorFilter}
            onChange={(event) => updateNavigatorFilter(event.target.value)}
          >
            <option value="all">My book · all navigators</option>
            {navigatorOptions.map((navigator) => (
              <option key={navigator} value={navigator}>
                {navigator}
              </option>
            ))}
          </DashboardSelect>
        </DashboardFilterField>
      </DashboardFilterRow>
      <PortfolioHealthQuadrant
        clinics={visibleClinics}
        targetRate={data.thresholds.alignmentTargetRate}
      />
      <PortfolioKpis
        summary={visibleSummary}
        reviewMaximumClinics={data.thresholds.reviewMaximumClinics}
        alignmentTargetRate={data.thresholds.alignmentTargetRate}
      />
      <ReviewBand
        clinics={visibleClinics}
        maximumClinics={data.thresholds.reviewMaximumClinics}
        kind="attention"
        isFiltered={clinicFilter !== "all" || navigatorFilter !== "all"}
      />
      <ReviewBand
        clinics={visibleClinics}
        maximumClinics={data.thresholds.reviewMaximumClinics}
        kind="opportunity"
        isFiltered={clinicFilter !== "all" || navigatorFilter !== "all"}
      />
      <ClinicScorecard clinics={visibleClinics} thresholds={data.thresholds} />
      <OperationsMetrics clinics={visibleClinics} />
      <p className="text-xs text-muted-foreground">
        Alignment uses marketable patients. Retell metrics use trailing-30-day outbound ended calls
        matched to a Carespace. Patient data coverage is available in its own tab.
      </p>
    </div>
  );
}
